export type StockControlFilter = {
    includeMain: boolean;
    includeCustom: boolean;
    includeTeam: boolean;
    includeReserve: boolean;
    categoryIds: number[];
};

export const defaultStockControlFilter = (): StockControlFilter => ({
    includeMain: true,
    includeCustom: true,
    includeTeam: true,
    includeReserve: true,
    categoryIds: [],
});

const storageKey = (userId: number): string => `voltox-${userId}-warehouse-stock-control-filter`;

const normalizeCategoryIds = (raw: unknown): number[] => {
    const values = Array.isArray(raw) ? raw : [];
    const ids = values.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0);
    return Array.from(new Set(ids));
};

const normalizeFilter = (value: unknown): StockControlFilter | null => {
    if (!value || typeof value !== 'object') return null;
    const row = value as Record<string, unknown>;
    const filter: StockControlFilter = {
        includeMain: row.includeMain !== false,
        includeCustom: row.includeCustom !== false,
        includeTeam: row.includeTeam !== false,
        includeReserve: row.includeReserve !== false,
        categoryIds: normalizeCategoryIds(row.categoryIds),
    };
    if (isDefaultStockControlFilter(filter)) return null;
    return filter;
};

export const isDefaultStockControlFilter = (filter: StockControlFilter): boolean =>
    filter.includeMain &&
    filter.includeCustom &&
    filter.includeTeam &&
    filter.includeReserve &&
    filter.categoryIds.length === 0;

export const loadStockControlFilter = (userId: number): StockControlFilter => {
    if (!userId || typeof window === 'undefined') return defaultStockControlFilter();
    try {
        const raw = window.localStorage.getItem(storageKey(userId));
        if (!raw) return defaultStockControlFilter();
        return normalizeFilter(JSON.parse(raw)) || defaultStockControlFilter();
    } catch {
        return defaultStockControlFilter();
    }
};

export const saveStockControlFilter = (userId: number, filter: StockControlFilter): void => {
    if (!userId || typeof window === 'undefined') return;
    try {
        if (isDefaultStockControlFilter(filter)) {
            window.localStorage.removeItem(storageKey(userId));
            return;
        }
        window.localStorage.setItem(storageKey(userId), JSON.stringify(filter));
    } catch {
        /* ignore quota / private mode */
    }
};
